import { type ComponentProps, type ReactElement } from 'react'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { ItemAlignContext, useItemGroupSize, type ItemAlign, type ItemSize } from './nested/context'
import { ItemGroup } from './nested/group'
import { ItemMedia } from './nested/media'
import { ItemContent } from './nested/content'
import { ItemTitle } from './nested/title'
import { ItemDescription } from './nested/description'
import { ItemActions } from './nested/actions'
import { itemDescriptionVariants, itemTitleClasses } from './styles'
import type { PartProps } from '../../lib/part-props'

/**
 * A settings row: what it is, what it means, and the one control that changes it. Flat, full
 * width, separated from its neighbours by a hairline when stacked in an Item.Group.
 *
 * Not a Cell, which is a raised card you press, and not a Field, which owns the wiring between
 * a label and an input. When the trailing control is an input, use a justified Field; it shares
 * this type and sits in the same group.
 */
const itemVariants = cva('group/item relative flex w-full min-w-0 text-left', {
  variants: {
    variant: {
      default: '',
      outline: 'border-border rounded-xl border px-3',
      muted: 'bg-background-subtle rounded-xl px-3',
    },
    size: {
      md: 'gap-3 py-3',
      sm: 'gap-2 py-2',
    },
    align: {
      center: 'items-center',
      start: 'items-start',
    },
    interactive: {
      // The hover wash is a layer behind the row, bled past the row's edges and rounded on its own,
      // so the group's hairline stays straight and full width and the row never leaves its column.
      true: [
        'isolate cursor-pointer bg-transparent outline-none',
        "before:content-[''] before:bg-background-subtle before:pointer-events-none before:absolute before:-inset-x-3 before:inset-y-0 before:-z-10 before:rounded-xl before:opacity-0 before:transition-opacity before:duration-150",
        'hover:before:opacity-100 focus-visible:before:opacity-100 focus-visible:focus-ring motion-reduce:before:transition-none',
      ].join(' '),
    },
  },
  defaultVariants: { variant: 'default', size: 'md', align: 'center' },
})

type ItemVariant = NonNullable<VariantProps<typeof itemVariants>['variant']>

interface ItemProps
  extends PartProps<ComponentProps<'div'>>, Omit<VariantProps<typeof itemVariants>, 'interactive' | 'size' | 'align'> {
  /** Density. Inherits from a surrounding Item.Group when omitted. */
  size?: ItemSize
  /** Where media and actions sit when the description wraps. */
  align?: ItemAlign
  /**
   * Render as another element, Base UI style: `render={<a href="…" />}` for a row that is a link,
   * `render={<button type="button" />}` for one that is a press. A rendered row is the target, so it
   * gets the hover wash and the focus ring.
   */
  render?: ReactElement
  /** Greys the title and description. Controls inside carry their own disabled. */
  disabled?: boolean
}

function Item({ className, variant, size, align = 'center', render, disabled, children, ...props }: ItemProps) {
  const groupSize = useItemGroupSize()

  const element = useRender({
    render,
    props: {
      'data-slot': 'item',
      'data-disabled': disabled ? '' : undefined,
      className: twMerge(
        itemVariants({ variant, size: size ?? groupSize, align, interactive: render ? true : undefined }),
        className,
      ),
      ...props,
      children,
    },
  })

  return <ItemAlignContext.Provider value={align}>{element}</ItemAlignContext.Provider>
}

Item.Group = ItemGroup
Item.Media = ItemMedia
Item.Content = ItemContent
Item.Title = ItemTitle
Item.Description = ItemDescription
Item.Actions = ItemActions

export { Item, itemVariants, itemTitleClasses, itemDescriptionVariants }
export type { ItemProps, ItemVariant, ItemSize, ItemAlign }
